import { AreaMap } from '../../domain/area-map';
import { MPCA } from '../../domain/mpca';
import { Zone } from '../../domain/zone';
import { mapper } from '../../tools/mapping/mapper';
import { AreaMapEntity, MPCAEntity, ZoneEntity } from '../entity';
import type { AppDB } from '../db';

export class ZoneRepository {
    constructor(private readonly database: AppDB) {}


    async updateZoneForConformity(zone: Zone) {
        let ze = await this.database.getZoneEntityByCode(zone.Code, zone.IsDepose);
        mapper.mutate(zone, ze, Zone, ZoneEntity);
        if (!ze || !ze.Id)
            return;

        if (ze.MPCAIds) {
            await this.database.clearMPCAById(ze.MPCAIds);
        }
        ze.MPCAIds = [];
        if (zone.MpcaList?.length > 0) {
            for (let i = 0; i < zone.MpcaList.length; i++) {
                let newmpcaId = await this.database.attachMPCAToZone(zone.MpcaList[i], ze.Id);
                ze.MPCAIds = ze.MPCAIds ? [...ze.MPCAIds, newmpcaId] : [newmpcaId];
            }
        }

        await this.database.zones.update(ze.Id, ze);
        return true;
    }

    async getZoneEntityByCode(code: string, isDepose: boolean) {
        return await this.database.zones.where('Code').equals(code).and(x => { return x.IsDepose === isDepose }).first(async ze => {
            return ze;
        });
    }

    async getZoneEntityById(id: number) {
        return await this.database.zones.where('Id').equals(id).first(async ze => {
            return ze;
        });
    }

    async getZoneByIdWithChildren(id: number) {
        return await this.database.zones.where('Id').equals(id).first(async ze => {
            const z = mapper.map(ze, ZoneEntity, Zone);
            if (ze?.MeasureIds) {
                for (let i = 0; i < ze.MeasureIds.length; i++) {
                    z.ZoneMesureList.push(await this.database.getZoneMeasureByIdWithChildren(ze.MeasureIds[i]));
                }
            }
            if (ze?.MPCAIds) {
                for (let i = 0; i < ze.MPCAIds.length; i++) {
                    const me = await this.database.getMPCAEntityById(ze.MPCAIds[i]);
                    const m = mapper.map(me, MPCAEntity, MPCA);
                    z.MpcaList.push(m);
                }
            }
            if (ze?.AreaMapIds) {
                for (let i = 0; i < ze.AreaMapIds.length; i++) {
                    const me = await this.database.getAreaMapEntityById(ze.AreaMapIds[i]);
                    const m = mapper.map(me, AreaMapEntity, AreaMap);
                    z.AreaMapList.push(m);
                }
            }
            return z;
        });
    }

    async getZoneByCodeWithChildren(code: string, isDepose: boolean) {
        return await this.database.zones.where('Code').equals(code).and(x => { return x.IsDepose === isDepose }).first(async ze => {
            const z = mapper.map(ze, ZoneEntity, Zone);
            if (ze?.MeasureIds) {
                for (let i = 0; i < ze.MeasureIds.length; i++) {
                    z.ZoneMesureList.push(await this.database.getZoneMeasureByIdWithChildren(ze.MeasureIds[i]));
                }
            }
            if (ze?.MPCAIds) {
                for (let i = 0; i < ze.MPCAIds.length; i++) {
                    const me = await this.database.getMPCAEntityById(ze.MPCAIds[i]);
                    const m = mapper.map(me, MPCAEntity, MPCA);
                    z.MpcaList.push(m);
                }
            }
            if (ze?.AreaMapIds) {
                for (let i = 0; i < ze.AreaMapIds.length; i++) {
                    const me = await this.database.getAreaMapEntityById(ze.AreaMapIds[i]);
                    const m = mapper.map(me, AreaMapEntity, AreaMap);
                    z.AreaMapList.push(m);
                }
            }
            return z;
        });
    }

    async attachZone(zone: Zone, interventionId: number) {
        let zoneId: number;
        const ze = mapper.map(zone, Zone, ZoneEntity);

        const ie = await this.database.getInterventionEntityById(interventionId);
        zoneId = await this.database.zones.add(ze);
        await this.database.interventions.update(interventionId, {
            ZoneIds: ie?.ZoneIds ? [...ie.ZoneIds, zoneId] : [zoneId]
        });

        if (zone.ZoneMesureList?.length > 0) {
            for (let i = 0; i < zone.ZoneMesureList.length; i++) {
                await this.database.attachMeasure(zone.ZoneMesureList[i], zoneId);
            }
        }

        if (zone.MpcaList?.length > 0) {
            for (let i = 0; i < zone.MpcaList.length; i++) {
                await this.database.attachMPCAToZone(zone.MpcaList[i], zoneId);
            }
        }
        if (zone.AreaMapList?.length > 0) {
            for (let i = 0; i < zone.AreaMapList.length; i++) {
                await this.database.attachAreaMapToZone(zone.AreaMapList[i], zoneId);
            }
        }
        return zoneId;
    }
}
